// useStickyLabel.js
import { useEffect, useRef, useState } from 'react';

const areLabelsSame = (a, b) => {
   if (a.length !== b.length) return false;
   return a.every((l, i) => l.label === b[i]?.label && l.type === b[i]?.type);
};

/**
 * Tracks one sticky item independently per type.
 *
 * Returns an array of sticky label objects sorted by their position in `data`
 * (i.e. the order they actually appear in the list), so callers can render
 * them stacked in the correct order regardless of scroll direction.
 *
 * @param {object}   options
 * @param {Ref}      options.containerRef      - ref pointing at the scroll container element
 * @param {Array}    options.data              - flat list of all items
 * @param {boolean}  options.inverted          - true when list is rendered bottom-up
 * @param {string[]} options.stickyTypes       - which item types should be tracked independently
 * @param {Function} options.getStickyData     - (item) => { label, type }
 * @param {string}   options.itemSelector      - CSS selector for rendered item elements
 * @param {Function} options.getIndex          - (el) => data index (number)
 * @param {number}   options.topThreshold      - px below container top that counts as "at top"
 *
 * @returns {Array<{ label: string, type: string }>} - sorted by data index, ascending
 */
export function useStickyLabel({
   containerRef,
   data = [],
   inverted = false,
   stickyTypes = ['date-label'],
   getStickyData = item => ({ label: item?.label, type: item?.type }),
   itemSelector = '[data-index]',
   getIndex = el => parseInt(el.dataset.index, 10),
   topThreshold = 80,
}) {
   // Array of sticky label objects: [{ label, type }, ...] sorted by data index
   const [stickyLabels, setStickyLabels] = useState([]);
   const rafIdRef = useRef(null);
   const updateFnRef = useRef(null);

   useEffect(() => {
      const getContainer = () => containerRef?.current;

      const updateStickyLabels = () => {
         const container = getContainer();
         if (!container || !data?.length) {
            setStickyLabels(prev => (prev.length === 0 ? prev : []));
            return;
         }

         const containerRect = container.getBoundingClientRect();
         const renderedItems = container.querySelectorAll(itemSelector);
         if (!renderedItems.length) return;

         // Per-type tracking: closest visible sticky item + virtualized fallback
         // closestByType: Map<type, { dataIndex, data, rect }>
         const closestByType = new Map();
         // anyItemAboveThreshold: do we have anything scrolled past the top yet?
         let anyItemAboveThreshold = false;

         renderedItems.forEach(el => {
            const index = getIndex(el);
            if (isNaN(index)) return;
            const item = data[index];

            const rect = el.getBoundingClientRect();
            if (rect.top <= containerRect.top + topThreshold) {
               anyItemAboveThreshold = true;
            }

            if (!item || !stickyTypes.includes(item.type)) return;
            if (rect.top > containerRect.top + topThreshold) return;

            const distance = Math.abs(rect.top - containerRect.top);
            const existing = closestByType.get(item.type);
            if (!existing || distance < existing.distance) {
               closestByType.set(item.type, { dataIndex: index, stickyData: getStickyData(item), rect, distance });
            }
         });

         // Build the result map: type -> { dataIndex, label, type }
         // We use Map so we can do one final sort by dataIndex at the end.
         const resultMap = new Map(); // type -> { dataIndex, label, type }

         // 1. Handle types that have a closest visible DOM element
         for (const [type, entry] of closestByType) {
            const isFullyScrolledOff = entry.rect.bottom <= containerRect.top;
            if (isFullyScrolledOff) {
               resultMap.set(type, { dataIndex: entry.dataIndex, ...entry.stickyData });
            }
         }

         // 2. Fallback for types whose label has been virtualized away (not in DOM)
         //    Only run when something has actually scrolled past the top.
         if (anyItemAboveThreshold) {
            const missingTypes = stickyTypes.filter(t => !resultMap.has(t) && !closestByType.has(t));

            if (missingTypes.length > 0) {
               // Sort rendered items to find first one at the top of the viewport
               const allItems = [...renderedItems].sort((a, b) => {
                  const ai = getIndex(a);
                  const bi = getIndex(b);
                  return inverted ? bi - ai : ai - bi;
               });

               if (allItems.length > 0) {
                  const topIndex = getIndex(allItems[0]);
                  // Walk from topIndex backward toward the start of data to find missing types
                  const step = inverted ? 1 : -1;
                  const remaining = new Set(missingTypes);

                  for (let i = topIndex; i >= 0 && i < data.length && remaining.size > 0; i += step) {
                     const item = data[i];
                     if (item && remaining.has(item.type)) {
                        resultMap.set(item.type, { dataIndex: i, ...getStickyData(item) });
                        remaining.delete(item.type);
                     }
                  }
               }
            }
         }

         // 3. Convert to array sorted by data index based on visual top
         const newLabels = [...resultMap.values()].sort((a, b) => {
            return inverted ? b.dataIndex - a.dataIndex : a.dataIndex - b.dataIndex;
         });

         setStickyLabels(prev => {
            if (newLabels.length === 0 && prev.length === 0) return prev;
            return areLabelsSame(prev, newLabels) ? prev : newLabels;
         });
      };

      // Store ref so scroll listener always uses the latest closure
      updateFnRef.current = updateStickyLabels;

      const container = getContainer();
      if (!container) return;

      const handleScroll = () => {
         if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
         rafIdRef.current = requestAnimationFrame(() => {
            rafIdRef.current = null;
            updateFnRef.current?.();
         });
      };

      container.addEventListener('scroll', handleScroll, { passive: true });

      // Fire at staggered intervals to catch layout settling after render
      const timers = [
         setTimeout(updateStickyLabels, 0),
         setTimeout(updateStickyLabels, 100),
         setTimeout(updateStickyLabels, 300),
         setTimeout(updateStickyLabels, 600),
      ];

      return () => {
         container.removeEventListener('scroll', handleScroll);
         if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
         timers.forEach(clearTimeout);
      };
   }, [containerRef, data, inverted, stickyTypes, getStickyData, itemSelector, getIndex, topThreshold]);

   // Polling fallback: re-trigger until layout is stable after initial data load
   useEffect(() => {
      if (!data?.length) return;

      let attempts = 0;
      const maxAttempts = 20;

      const poll = setInterval(() => {
         attempts++;
         const container = containerRef?.current;
         if (container) {
            updateFnRef.current?.();
         }
         if (attempts >= maxAttempts) clearInterval(poll);
      }, 100);

      return () => clearInterval(poll);
      // Only re-run when data goes from empty to non-empty (initial load)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [!!data?.length]);

   return stickyLabels;
}
